import {
  parseBalanceData,
  parseBalanceFormData,
  parseLCAData,
  parseLCAFormData,
  parseTEAFormData,
  parseTEAData,
  parseCashFlowsData,
  parseMCData,
  parseMCFormData,
  parseCharacteristicsData,
  sortNodesByAnchor,
  parseLevelization,
  parseSensitivityData,
  parseSensitivityLCAFormData,
  parseSensitivityTEAFormData,
  parseCharacteristicsFormData,
  parseCostBreakdownData,
  parseCostBreakdownFormData,
  parseLCATimeseriesData,
  parseCarbonChainData,
} from '@/utils/analysisUtils';
import { create } from 'zustand';
import { immer } from 'zustand/middleware/immer';
import { subscribeWithSelector } from 'zustand/middleware';
import { keyBy } from '@/utils/miscUtils';
import { getAnalysisResults, getAnalysisTypeKeys, retrieveAnalysis } from '@/api/analyses';
import { applyNodeInfo, getEmissionTypes } from '@/utils/pathwayUtils';
import { getPathway } from '@/api/pathways';
import { getCase } from '@/api/cases';
import { workbenchActions } from './workbenchStore';
import { caseActions } from './caseStore';
import useAnalysisStatusStore, { analysisStatusActions } from './analysisStatusStore';
import { ANALYSIS_STATUS } from '@/consts';
import { toast } from '@/utils/toast';

const initialValues = {
  nodes: [],
  nodeHierarchy: [],
  lca: {},
  tea: {},
  tea_breakdown: {},
  carbon_chain: {},
  balance: {},
  characteristics: {},
  mc: {},
  cash_flows: {},
  sensitivity_lca: {},
  sensitivity_tea: {},
  lca_timeseries: {},
};

const chartParsers = {
  lca: parseLCAData,
  tea: parseTEAData,
  tea_breakdown: parseCostBreakdownData,
  balance: parseBalanceData,
  characteristics: parseCharacteristicsData,
  mc: parseMCData,
  cash_flows: parseCashFlowsData,
  sensitivity_lca: parseSensitivityData,
  sensitivity_tea: parseSensitivityData,
  sensitivity: parseSensitivityData,
  lca_timeseries: parseLCATimeseriesData,
};

const formDataParsers = {
  lca: parseLCAFormData,
  tea: parseTEAFormData,
  balance: parseBalanceFormData,
  characteristics: parseCharacteristicsFormData,
  tea_breakdown: parseCostBreakdownFormData,
  mc: parseMCFormData,
  cash_flows: parseTEAFormData,
  sensitivity_lca: parseSensitivityLCAFormData,
  sensitivity_tea: parseSensitivityTEAFormData,
  lca_timeseries: parseLCAFormData,
};

const useAnalysisStore = create()(subscribeWithSelector(immer(() => initialValues)));
const { setState: set, getState: get } = useAnalysisStore;

// actions
export const analysisActions = {
  init: async pathwayId => {
    analysisActions.clear();

    const { data: pathway } = await getPathway(pathwayId);
    const [{ data: caseData }, { data: analysis }] = await Promise.all([
      getCase(pathway.case_id),
      retrieveAnalysis(pathway.analysis_id),
    ]);

    const { nodes: initialNodes } = pathway;
    const { anchor, node_info, node_hierarchy } = caseData;
    const nodes = applyNodeInfo(initialNodes, node_info);
    const sortedNodes = sortNodesByAnchor(nodes, anchor.node_id);

    set({
      id: analysis.id,
      caseId: pathway.case_id,
      name: pathway.name,
      pathwayId: pathway.id,
      nodes: keyBy(sortedNodes, 'id'),
      // includes the nodes of any internal pathways, nested as `children`
      nodeHierarchy: node_hierarchy ?? [],
    });

    // subscribe to analysis status changes
    const subscription = useAnalysisStatusStore.subscribe(
      state => state.analysisTypes,
      (analysisTypes, previousAnalysisTypes) => {
        Object.entries(analysisTypes).forEach(([type, analysisType]) => {
          const hasNewlySucceeded =
            analysisType?.status === ANALYSIS_STATUS.succeeded &&
            previousAnalysisTypes?.[type]?.status !== ANALYSIS_STATUS.succeeded;

          if (hasNewlySucceeded) {
            analysisActions.handleAnalysisSuccess(type);
          }
        });
      },
    );

    workbenchActions.init(pathway, caseData, analysis);
    caseActions.init(caseData);
    analysisStatusActions.addSubscription(subscription);

    return pathway;
  },

  handleAnalysisSuccess: type => {
    if (type === 'sensitivity') {
      analysisActions.addSensitivityPlot('lca');
      analysisActions.addSensitivityPlot('tea');
    } else {
      if (type === 'lca') {
        analysisActions.addPlot(type, { levelize: false, storeKeys: ['balance'] });
        analysisActions.addPlot(type, { storeKeys: ['lca'] });
        analysisActions.addTimeseriesPlot('lca_timeseries', { storeKeys: ['lca_timeseries'] });
      } else {
        analysisActions.addPlot(type, { storeKeys: getAnalysisTypeKeys(type) });
      }

      if (type === 'tea') {
        analysisActions.addPlot('tea_breakdown', { storeKeys: getAnalysisTypeKeys('tea_breakdown') });
        const firstNodeId = Object.values(get().nodes ?? {})?.[0]?.id;
        analysisActions.loadCarbonChain(firstNodeId);
      }
    }
  },

  getResults: async (plotType, params) => {
    const { id, nodes } = get();
    const resultParser = chartParsers[plotType];

    try {
      const { data } = await getAnalysisResults(id, plotType, params);
      return {
        results: resultParser(data, nodes, params),
        data,
      };
    } catch (error) {
      toast.error(error.response?.data?.message ?? 'Unable to load analysis results.');
      return { results: null, data: null };
    }
  },

  addSensitivityPlot: async type => {
    const { id, nodes } = get();
    const node = Object.values(nodes)?.[0] ?? {};
    const nodeId = node.id;
    const plotType = `sensitivity_${type}`;

    const params = {
      node_id: nodeId,
      analysis_type: type,
    };

    const { data } = await getAnalysisResults(id, plotType, params);
    const { levelization, ...rest } = data ?? {};

    const resultParser = chartParsers[plotType];
    const results = resultParser(data, nodes, params);

    let plotData = {
      nodeId,
    };

    if (levelization) {
      plotData = { ...plotData, ...parseLevelization(levelization) };
    }

    set(state => {
      state[plotType] = { ...plotData, results, ...rest };
    });
  },

  addTimeseriesPlot: async (plotType, { storeKeys = [] } = {}) => {
    const { id, nodes = {} } = get();
    const node = Object.values(nodes)?.[0] ?? {};
    const nodeId = node.id;
    const params = {
      node_id: nodeId,
      levelization: null,
    };

    const { data } = await getAnalysisResults(id, plotType, params);
    const { cumulative } = data ?? {};

    // eslint-disable-next-line no-unused-vars
    const { levelization, levelizable_ios, ...rest } = cumulative?.[0] ?? {};
    const emissionTypeOptions = getEmissionTypes(Object.values(nodes));
    const emissionType = emissionTypeOptions?.[0]?.value;

    let plotData = {
      nodeId,
      emissionType,
      levelize: 'false',
    };

    if (levelization) {
      plotData = { ...plotData, ...parseLevelization(levelization), levelize: 'true' };
    }

    for (let key of storeKeys) {
      const resultParser = chartParsers[key];
      const results = resultParser(data, nodes, params);

      set(state => {
        state[key] = { ...plotData, results, ...rest };
      });
    }
  },

  addPlot: async (plotType, { levelize, storeKeys = [] } = {}) => {
    const { id, nodes = {} } = get();
    const node = Object.values(nodes)?.[0] ?? {};
    const nodeId = node.id;
    const params = {
      node_id: nodeId,
    };

    if (levelize === false) {
      params.levelization = null;
    }

    const { data } = await getAnalysisResults(id, plotType, params);

    const { levelization, ...rest } = data ?? {};
    const emissionTypeOptions = getEmissionTypes(Object.values(nodes));
    const emissionType = emissionTypeOptions?.[0]?.value;

    let plotData = {
      nodeId,
      emissionType,
    };

    if (levelization) {
      plotData = { ...plotData, ...parseLevelization(levelization), levelize: 'true' };
    }

    for (let key of storeKeys) {
      const resultParser = chartParsers[key];
      const results = resultParser(data, nodes, params);

      set(state => {
        state[key] = { ...plotData, results, ...rest };
      });
    }
  },

  loadCarbonChain: async nodeId => {
    const { id } = get();

    if (!id || !nodeId) {
      return;
    }

    set(state => {
      state.carbon_chain = { ...state.carbon_chain, nodeId, pending: true };
    });

    let data = null;
    try {
      ({ data } = await getAnalysisResults(id, 'carbon_chain', { node_id: nodeId }));
    } catch {
      data = null;
    }

    set(state => {
      state.carbon_chain = {
        nodeId,
        pending: false,
        results: parseCarbonChainData(data),
      };
    });
  },

  updatePlot: async (plotType, plotFormData) => {
    const params = formDataParsers[plotType](plotFormData);
    const storeData = get()[plotType];

    if (storeData.nodeId !== plotFormData.nodeId) {
      params.levelization = undefined;
    }

    if (plotFormData.levelize === 'false') {
      if (plotType === 'mc') {
        delete params.levelization;
      } else {
        params.levelization = null;
      }
    }

    const { results, data } = await analysisActions.getResults(plotType, params);
    if (!data) {
      return;
    }
    const { levelization, ...rest } = data;
    let plotData = {
      ...plotFormData,
      ...(plotType === 'mc' && plotFormData.levelize === 'false'
        ? { levelizeBy: undefined, levelizeUnit: undefined }
        : {}),
      results,
      ...rest,
    };

    if (levelization) {
      plotData = { ...plotData, ...parseLevelization(levelization) };
    }

    if (data.distribution === 'levelized_cost') {
      plotData.scalar = data.scalar || 1;
    }

    set(state => {
      state[plotType] = plotData;
    });
  },

  clear: () => set(initialValues, true),
};

export const useAnalysis = () => useAnalysisStore(store => ({ id: store.id, name: store.name, caseId: store.caseId }));
export const useNodeById = nodeId => useAnalysisStore(store => store?.nodes?.[nodeId]);
export const useNodes = () => useAnalysisStore(store => Object.values(store?.nodes));
export const useNodeHierarchy = () => useAnalysisStore(store => store?.nodeHierarchy);

export const useLCAPlot = () => useAnalysisStore(store => store?.lca);
export const useLCATimeseriesPlot = () => useAnalysisStore(store => store?.lca_timeseries);
export const useTEAPlot = () => useAnalysisStore(store => store?.tea);
export const useMCPlot = () => useAnalysisStore(store => store?.mc);
export const useCashFlowsPlot = () => useAnalysisStore(store => store?.cash_flows);
export const useBalance = () => useAnalysisStore(store => store?.balance);
export const useCharacteristics = () => useAnalysisStore(store => store?.characteristics);
export const useCostBreakdown = () => useAnalysisStore(store => store?.tea_breakdown);
export const useCarbonChain = () => useAnalysisStore(store => store?.carbon_chain);
export const useSensitivityPlot = type => useAnalysisStore(store => store?.[`sensitivity_${type}`]);

export default useAnalysisStore;
